import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRoleTranslation } from '../hooks/useRoleTranslation';
import { ClockIcon, TrashIcon, EyeIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../services/apiClient';
import ErrorNotification from '../components/common/ErrorNotification';
import LoadingOverlay from '../components/common/LoadingOverlay';

const History = () => {
  const { t } = useTranslation('common');
  const { getRoleLabel } = useRoleTranslation();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const data = await apiClient.get('/api/consultation-history');
      setHistory(data);
    } catch (err) {
      console.error('Error fetching consultation history:', err);
      setError(t('history.errors.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      setDeletingId(itemToDelete.id);
      await apiClient.delete(`/api/consultation-history/${itemToDelete.id}`);
      setHistory(history.filter(item => item.id !== itemToDelete.id));
      setShowDeleteDialog(false);
      setItemToDelete(null);
    } catch (err) {
      console.error('Error deleting consultation history:', err);
      setError(t('history.errors.deleteFailed'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false);
    setItemToDelete(null);
  };

  const handleView = (item) => {
    // Navigate to Consultation page with history data
    navigate('/consultation', { 
      state: { 
        historyData: item 
      } 
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("fr-CH", {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getPreviewText = (text, maxLength = 100) => {
    if (!text) return t('history.noContent');
    // Remove HTML tags for preview
    const strippedText = text.replace(/<[^>]*>/g, '');
    if (strippedText.length <= maxLength) return strippedText;
    return strippedText.substring(0, maxLength) + '...';
  };

  if (loading) {
    return <LoadingOverlay message={t('history.loading')} />;
  }

  return (
    <div className="h-full flex flex-col bg-white">
      <ErrorNotification
        show={!!error}
        message={error}
        onClose={() => setError('')}
      />

      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <h1 className="text-2xl font-semibold text-gray-900">{t('history.title')}</h1>
        <p className="mt-2 text-sm text-gray-600">{t('history.description')}</p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {history.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <ClockIcon className="w-16 h-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('history.empty.title')}</h3>
            <p className="text-sm text-gray-500 mb-6">{t('history.empty.description')}</p>
            <button
              onClick={() => navigate('/consultation')}
              className="px-4 py-2 bg-[rgb(141,212,209)] text-gray-700 rounded-md hover:bg-[rgb(121,192,189)] transition-colors"
            >
              {t('history.empty.action')}
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {history.map((item) => (
              <div
                key={item.id}
                className="bg-gray-50 rounded-lg p-6 border border-gray-200 hover:border-gray-300 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    {/* Header */}
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {getRoleLabel(`role-${item.selected_role.toLowerCase()}`)}
                      </span>
                      <span className="text-sm font-medium text-gray-700">
                        ({item.employee_name || item.employee_id})
                      </span>
                      <span className="text-sm text-gray-500">
                        {formatDate(item.created_at)}
                      </span>
                    </div>

                    {/* Content Preview - Show dictaphone if available, otherwise transcript */}
                    <div className="space-y-2">
                      {item.dictaphone_text ? (
                        <div>
                          <span className="text-xs font-medium text-gray-500 uppercase">
                            {t('history.preview.dictaphone')}:
                          </span>
                          <p className="text-sm text-gray-700 mt-1">
                            {getPreviewText(item.dictaphone_text)}
                          </p>
                        </div>
                      ) : item.base_transcript ? (
                        <div>
                          <span className="text-xs font-medium text-gray-500 uppercase">
                            {t('history.preview.transcript')}:
                          </span>
                          <p className="text-sm text-gray-700 mt-1">
                            {getPreviewText(item.base_transcript)}
                          </p>
                        </div>
                      ) : null}
                    </div>

                    {/* Data availability pills */}
                    <div className="mt-3 flex items-center flex-wrap gap-2">
                      {/* Transcript pill */}
                      <span 
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          item.base_transcript 
                            ? 'bg-orange-100 text-orange-800' 
                            : 'bg-gray-100 text-gray-400'
                        }`}
                        title={item.base_transcript ? t('history.hasTranscript') : t('history.noTranscript')}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                          item.base_transcript ? 'bg-orange-500' : 'bg-gray-300'
                        }`}></span>
                        {t('history.dataTypes.transcript')}
                      </span>

                      {/* Dictaphone pill */}
                      <span 
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          item.dictaphone_text 
                            ? 'bg-yellow-100 text-yellow-800' 
                            : 'bg-gray-100 text-gray-400'
                        }`}
                        title={item.dictaphone_text ? t('history.hasDictaphone') : t('history.noDictaphone')}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                          item.dictaphone_text ? 'bg-yellow-500' : 'bg-gray-300'
                        }`}></span>
                        {t('history.dataTypes.dictaphone')}
                      </span>

                      {/* File report pill */}
                      <span 
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          item.file_report 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-400'
                        }`}
                        title={item.file_report ? t('history.hasReport') : t('history.noReport')}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                          item.file_report ? 'bg-green-500' : 'bg-gray-300'
                        }`}></span>
                        {t('history.dataTypes.fileReport')}
                      </span>

                      {/* Patient report pill */}
                      <span 
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          item.patient_report 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-gray-100 text-gray-400'
                        }`}
                        title={item.patient_report ? t('history.hasMail') : t('history.noMail')}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                          item.patient_report ? 'bg-blue-500' : 'bg-gray-300'
                        }`}></span>
                        {t('history.dataTypes.patientReport')}
                      </span>

                      {/* Insurance pill */}
                      <span 
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          item.insurance_data 
                            ? 'bg-purple-100 text-purple-800' 
                            : 'bg-gray-100 text-gray-400'
                        }`}
                        title={item.insurance_data ? t('history.hasInsurance') : t('history.noInsurance')}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                          item.insurance_data ? 'bg-purple-500' : 'bg-gray-300'
                        }`}></span>
                        {t('history.dataTypes.insurance')}
                      </span>

                      {/* Quote pill */}
                      {item.quote_text !== undefined && (
                        <span 
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            item.quote_text 
                              ? 'bg-indigo-100 text-indigo-800' 
                              : 'bg-gray-100 text-gray-400'
                          }`}
                          title={item.quote_text ? t('history.hasQuote') : t('history.noQuote')}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                            item.quote_text ? 'bg-indigo-500' : 'bg-gray-300'
                          }`}></span>
                          {t('history.dataTypes.quote')}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleView(item)}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title={t('history.actions.view')}
                    >
                      <EyeIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(item)}
                      disabled={deletingId === item.id}
                      className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                      title={t('history.actions.delete')}
                    >
                      {deletingId === item.id ? (
                        <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
                      ) : (
                        <TrashIcon className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    {t('history.delete.title', 'Confirmer la suppression')}
                  </h3>
                </div>
              </div>
              <div className="mb-6">
                <p className="text-sm text-gray-500">
                  {t('history.delete.message', 'Êtes-vous sûr de vouloir supprimer cette consultation de l\'historique ? Cette action est irréversible.')}
                </p>
                {itemToDelete && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-md">
                    <p className="text-xs text-gray-600 mb-1">
                      {t('history.delete.details', 'Consultation du')} {formatDate(itemToDelete.created_at)}
                    </p>
                    <p className="text-xs text-gray-600">
                      {t('history.delete.role', 'Rôle')}: {getRoleLabel(`role-${itemToDelete.selected_role.toLowerCase()}`)}
                    </p>
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleDeleteCancel}
                  disabled={deletingId}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={deletingId}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {deletingId ? (
                    <div className="flex items-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      {t('common.deleting', 'Suppression...')}
                    </div>
                  ) : (
                    t('common.delete')
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default History;

